/**
 * Building the de-duplicated project list a picker offers.
 *
 * Split out of `NewThreadLanding.logic.ts` when it went past the file cap. The
 * merge of three overlapping sources, and each source's idea of what counts as
 * a repeat, is one subject and reads better on its own.
 */
import {
  projectSelectionKey,
  type DesktopRuntimeInventoryProjectSummary,
  type ProjectSummary,
} from "./NewThreadLanding.logic";

/**
 * Build the de-duplicated project list shown in the /new picker.
 *
 * - `connectedProjects` (local "This Device" + already-connected cloud VMs) are
 *   de-duplicated by workspace path (`cwd`), so the same repo registered across
 *   multiple connected environments — or across server restarts — appears
 *   once instead of as N "server" rows.
 * - `cloudCatalogRows` are stopped / not-yet-started cloud projects from the
 *   CMMD catalog. They're appended so cloud projects are selectable even when
 *   their VM isn't connected, de-duplicated by `environmentId` against the
 *   already-connected set so a running VM doesn't appear twice. They carry a
 *   "Cloud" subLabel; the cloud-vs-local glyph is derived from the
 *   `cmmd-runtime:` environmentId prefix at render time.
 */
interface PickerConnectedProject {
  readonly id: string;
  readonly environmentId: string;
  readonly name: string;
  readonly cwd: string;
}

export interface PickerCloudCatalogRow {
  /** Stable per-project identity; used as the picker entry id. */
  readonly projectKey: string;
  readonly environmentId: string;
  readonly projectName: string;
  /**
   * The row's workspace path, used to recognise the connected project it
   * already describes. A row with no resolved ref cannot be matched by id, and
   * without this the same project was offered twice: once as the checkout the
   * runtime reports and once as the catalog row behind it.
   */
  readonly workspaceRoot?: string | null;
  readonly resolvedProjectId?: string | null;
  readonly icon?: string | null;
  readonly iconUrl?: string | null;
}

function indexCatalogRowsByResolvedProject(
  rows: readonly PickerCloudCatalogRow[],
): ReadonlyMap<string, PickerCloudCatalogRow> {
  return new Map(
    rows.flatMap((row) =>
      row.resolvedProjectId
        ? [[`${row.environmentId}:${row.resolvedProjectId}`, row] as const]
        : [],
    ),
  );
}

function buildConnectedProjectSummary(
  project: PickerConnectedProject,
  catalogRow: PickerCloudCatalogRow | undefined,
): ProjectSummary {
  return catalogRow
    ? {
        id: catalogRow.projectKey,
        environmentId: catalogRow.environmentId,
        name: project.name,
        resolvedProjectId: project.id,
        subLabel: "Cloud",
        cwd: project.cwd,
        icon: catalogRow.icon ?? null,
        iconUrl: catalogRow.iconUrl ?? null,
      }
    : {
        id: project.id,
        environmentId: project.environmentId,
        name: project.name,
        cwd: project.cwd,
      };
}

/**
 * A cloud project that has no runtime yet.
 *
 * It has no checkout to read a favicon from, so CMMD's identity is the only
 * mark it can carry.
 *
 * Exported for `draftPickerProjectSummaries.ts`, which now decides pre-connect
 * visibility with `components/projects/visibleCloudRows.ts` before this file
 * ever sees a row. `buildPickerProjectSummaries`'s own de-dup below (the
 * ledger, `indistinguishableRowEnvironments`) assumes its `cloudCatalogRows`
 * are the RAW catalog and works out for itself which ones a connected project
 * already covers — right for a caller with no better answer, wrong once one
 * exists: an unconnected sibling Registration whose row has no `workspaceRoot`
 * reads as "ambiguous" to that heuristic and gets silently claimed by ANY
 * connected project sharing its environment, which is the same over-exclusion
 * the sidebar's runtime-level check used to make, arrived at differently. A
 * caller that already excluded represented rows by Registration identity maps
 * what is left through this function directly, bypassing that heuristic.
 */
export function buildUnconnectedCloudProjectSummary(row: PickerCloudCatalogRow): ProjectSummary {
  return {
    // Project key, not runtime id: an unprovisioned project has no runtime,
    // and this id must stay stable once one is created on the first thread.
    id: row.projectKey,
    environmentId: row.environmentId,
    name: row.projectName,
    ...(row.resolvedProjectId !== undefined ? { resolvedProjectId: row.resolvedProjectId } : {}),
    subLabel: "Cloud",
    icon: row.icon ?? null,
    iconUrl: row.iconUrl ?? null,
  };
}

/**
 * Tracks which projects a picker list has already offered.
 *
 * Three sources describe overlapping sets, and each one recognises a repeat
 * differently: a checkout by its path on a runtime, a catalog row by its
 * project key or by the checkout it describes, a desktop row by its scoped id.
 */
class PickerProjectLedger {
  private readonly seenCwd = new Set<string>();
  private readonly seenProjectKey = new Set<string>();

  recordCwd(environmentId: string, cwd: string): boolean {
    if (cwd.length === 0) return true;
    const runtimeCwd = `${environmentId}:${cwd}`;
    if (this.seenCwd.has(runtimeCwd)) return false;
    this.seenCwd.add(runtimeCwd);
    return true;
  }

  recordProjectKey(key: string): boolean {
    if (this.seenProjectKey.has(key)) return false;
    this.seenProjectKey.add(key);
    return true;
  }

  hasCwd(environmentId: string, cwd: string | null | undefined): boolean {
    return (cwd ?? "").length > 0 && this.seenCwd.has(`${environmentId}:${cwd}`);
  }

  recordConnectedOn(environmentId: string): void {
    this.connectedByEnvironment.set(
      environmentId,
      (this.connectedByEnvironment.get(environmentId) ?? 0) + 1,
    );
  }

  /**
   * Spends one of the runtime's connected projects against this row.
   *
   * Used only when a runtime's rows cannot be told apart. The row describes one
   * of the projects already on the list, and there is no way to say which, so
   * the count is all that can be honoured: as many rows as there are connected
   * projects are treated as already offered, and any surplus is a project with
   * no checkout yet and still needs an entry.
   */
  claimConnectedOn(environmentId: string): boolean {
    const remaining = this.connectedByEnvironment.get(environmentId) ?? 0;
    if (remaining === 0) return false;
    this.connectedByEnvironment.set(environmentId, remaining - 1);
    return true;
  }

  private readonly connectedByEnvironment = new Map<string, number>();
}

/**
 * The runtimes whose catalog rows cannot be told apart.
 *
 * A row's `workspaceRoot` is the SPRITE root rather than the project directory
 * on some paths, so every row on a per-owner runtime can report the same one.
 * Where that happens no row names a project, and matching one to the checkout
 * it describes is impossible (FOR-36358).
 */
function indistinguishableRowEnvironments(
  rows: readonly PickerCloudCatalogRow[],
): ReadonlySet<string> {
  const rootsByEnvironment = new Map<string, string[]>();
  for (const row of rows) {
    if (row.resolvedProjectId) continue;
    rootsByEnvironment.set(row.environmentId, [
      ...(rootsByEnvironment.get(row.environmentId) ?? []),
      row.workspaceRoot ?? "",
    ]);
  }
  const environments = new Set<string>();
  for (const [environmentId, roots] of rootsByEnvironment) {
    if (roots.length > 1 && new Set(roots).size < roots.length) environments.add(environmentId);
    if (roots.some((root) => root.length === 0)) environments.add(environmentId);
  }
  return environments;
}

export function buildPickerProjectSummaries(input: {
  readonly connectedProjects: readonly PickerConnectedProject[];
  readonly cloudCatalogRows: readonly PickerCloudCatalogRow[];
  readonly desktopInventoryProjects: readonly DesktopRuntimeInventoryProjectSummary[];
}): ProjectSummary[] {
  const summaries: ProjectSummary[] = [];
  const ledger = new PickerProjectLedger();
  const catalogByResolvedProject = indexCatalogRowsByResolvedProject(input.cloudCatalogRows);

  for (const project of input.connectedProjects) {
    if (!ledger.recordCwd(project.environmentId, project.cwd)) continue;
    const catalogRow = catalogByResolvedProject.get(`${project.environmentId}:${project.id}`);
    const summary = buildConnectedProjectSummary(project, catalogRow);
    ledger.recordProjectKey(projectSelectionKey(summary));
    ledger.recordConnectedOn(project.environmentId);
    summaries.push(summary);
  }

  const indistinguishable = indistinguishableRowEnvironments(input.cloudCatalogRows);
  for (const row of input.cloudCatalogRows) {
    // The checkout this row describes is already on the list, offered as the
    // project the runtime reports. Without this the same project appeared
    // twice, because a row with no resolved ref cannot be matched by id.
    if (ledger.hasCwd(row.environmentId, row.workspaceRoot)) continue;
    if (indistinguishable.has(row.environmentId) && ledger.claimConnectedOn(row.environmentId)) {
      continue;
    }
    if (!ledger.recordProjectKey(row.projectKey)) continue;
    summaries.push(buildUnconnectedCloudProjectSummary(row));
  }

  for (const project of input.desktopInventoryProjects) {
    if (!ledger.recordProjectKey(`${project.environmentId}:${project.id}`)) continue;
    summaries.push({
      id: project.id,
      environmentId: project.environmentId,
      name: project.name,
      subLabel: project.runtimeLabel,
    });
  }

  return summaries;
}
